import * as React from "react";

import { OrgSecretPicker } from "@/Settings/Secrets/OrgSecretPicker";
import { useSetMcpOrgCredential } from "@/shared/domain/mcp/hooks/useSetMcpOrgCredential";
import { Dialog, RobotoButton } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./OrgTokenDialog.module.css";

interface OrgTokenDialogProps {
  orgId: string;
  serverId: string;
  serverName: string;
  /** Org secret behind the saved org token; undefined when none is set. */
  currentSecretName: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Lets an org admin pick the org secret that holds an MCP server's org-wide
 * token. Service users call the server with that token, and so do members
 * whose own connection is missing or fails.
 */
export function OrgTokenDialog({
  orgId,
  serverId,
  serverName,
  currentSecretName,
  open,
  onOpenChange,
}: OrgTokenDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content>
        {/* Mounted only while open, so each opening starts from the saved
            secret rather than the last unsaved pick. */}
        {open && (
          <OrgTokenForm
            orgId={orgId}
            serverId={serverId}
            serverName={serverName}
            currentSecretName={currentSecretName}
            onDone={() => onOpenChange(false)}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function OrgTokenForm({
  orgId,
  serverId,
  serverName,
  currentSecretName,
  onDone,
}: Omit<OrgTokenDialogProps, "open" | "onOpenChange"> & {
  onDone: () => void;
}) {
  const setCredential = useSetMcpOrgCredential(orgId);
  const [secretName, setSecretName] = React.useState(currentSecretName ?? "");

  const handleSave = () => {
    setCredential.mutate({ serverId, secretName }, { onSuccess: onDone });
  };

  return (
    <div className={styles.dialogBody}>
      <Dialog.Title>{serverName} org token</Dialog.Title>
      <p className={styles.note}>
        Members&apos; own connections are tried first. This token is used when a
        member hasn&apos;t connected {serverName} or their connection fails, and
        always by service users such as agents launched by triggers.
      </p>
      <OrgSecretPicker
        orgId={orgId}
        legend="Token secret"
        description="The org secret holding the token."
        addSecretLabel="Add the token as a secret"
        value={secretName}
        onValueChange={setSecretName}
        disabled={setCredential.isPending}
      />
      {setCredential.isError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(setCredential.error)}
        </p>
      )}
      <div className={styles.actions}>
        <RobotoButton
          variant="secondary"
          onClick={onDone}
          disabled={setCredential.isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          onClick={handleSave}
          loading={setCredential.isPending}
          disabled={secretName === ""}
        >
          Save
        </RobotoButton>
      </div>
    </div>
  );
}
